// React imports
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Local imports
import { CONNECTOR_GROUPS, RUNS_ON_LABELS } from './elariomData.js';
import { SectionTitle, GroupTitle, StatusPill, ListRow } from './ElariomParts.js';
import { INK, MUTED } from './elariomTheme.js';

// Which server answers this workspace's Elariom. All servers share the same data, so exactly
// one of them should; moving it is one click, with nothing to change on the servers.
function RunsOn({ value, org, accesstoken, onChange }) {
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState(null);
    async function choose(e, runsOn) {
        if (!runsOn || runsOn === value) return;
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.put(`/api/elariom/teams/runs-on?accesstoken=${accesstoken}&organizationId=${org.id}`, { runsOn });
            onChange?.(data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        }
        setSaving(false);
    }
    // Stops this workspace's Elariom answering in Teams. "Connect" then signs it in again, here or
    // in another workspace (connecting there also disconnects it here).
    async function disconnect() {
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.delete(`/api/elariom/teams?accesstoken=${accesstoken}&organizationId=${org.id}`);
            onChange?.(data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        }
        setSaving(false);
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '6px' }}>
            <Typography sx={{ fontSize: '12px', color: MUTED }}>Runs on</Typography>
            <ToggleButtonGroup size='small' exclusive value={value} onChange={choose} disabled={saving}>
                {Object.entries(RUNS_ON_LABELS).map(([k, label]) => (
                    <ToggleButton key={k} value={k} sx={{ textTransform: 'none', fontSize: '12px', padding: '2px 10px' }}>{label}</ToggleButton>
                ))}
            </ToggleButtonGroup>
            <Button size='small' onClick={disconnect} disabled={saving}>Disconnect</Button>
            {error && <Typography sx={{ fontSize: '12px', color: '#A3402F' }}>{error}</Typography>}
        </Box>
    );
}

// Connects this workspace's WhatsApp number: the Meta phone number ID (WhatsApp Manager, under
// the number), and the webhook address to enter once in the Meta app.
function WhatsappSetup({ status, org, accesstoken, onChange }) {
    const [ id, setId ] = useState(status.phoneNumberId || '');
    const [ saving, setSaving ] = useState(false);
    const [ error, setError ] = useState(null);
    async function save(value) {
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.put(`/api/elariom/whatsapp/number?accesstoken=${accesstoken}&organizationId=${org.id}`, { phoneNumberId: value });
            onChange?.(data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message);
        }
        setSaving(false);
    }
    const webhook = `${window.location.origin}${status.webhook}`;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <TextField id={`whatsapp-number-${org.id}`} size='small' placeholder='Phone number ID' value={id} onChange={e => setId(e.target.value)} sx={{ width: '220px' }} inputProps={{ style: { fontSize: '12px' } }} />
                <Button variant='outlined' size='small' onClick={() => save(id)} disabled={saving || !id.trim() || id.trim() === status.phoneNumberId}>{status.connected ? 'Change' : 'Connect'}</Button>
                {status.connected && <Button size='small' onClick={() => { setId(''); save(''); }} disabled={saving}>Disconnect</Button>}
            </Box>
            <Typography sx={{ fontSize: '11px', color: MUTED }}>Webhook for the Meta app: {webhook}</Typography>
            {error && <Typography sx={{ fontSize: '12px', color: '#A3402F' }}>{error}</Typography>}
        </Box>
    );
}

// One connector as a list row: icon, name and what it does, its state, and one action.
function ConnectorRow({ connector, org, accesstoken, onTeamsChange, onWhatsappChange }) {
    const navigate = useNavigate();
    const planned = connector.status === 'planned';
    const action = connector.connect
        ? <Tooltip title={connector.connect.hint}><Button variant='outlined' size='small' href={connector.connect.href}>{connector.connect.label}</Button></Tooltip>
        : connector.settings
        ? <Button variant='outlined' size='small' onClick={() => navigate(`/organization/${org.alias}/settings`)}>Settings</Button>
        : planned
            ? <Tooltip title='Not built yet'><span><Button variant='contained' size='small' disabled>Connect</Button></span></Tooltip>
            : null;
    return (
        <ListRow
            icon={<FontAwesomeIcon icon={connector.icon} />}
            title={connector.name}
            titleAfter={<Typography component='span' sx={{ fontSize: '12px', color: MUTED }}>{connector.vendor}</Typography>}
            subtitle={connector.what}
            faded={planned}
            right={action}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '4px' }}>
                <StatusPill status={connector.status} />
                {connector.detail && <Typography sx={{ fontSize: '12px', color: MUTED }}>{connector.detail}</Typography>}
            </Box>
            {connector.runsOn && <RunsOn value={connector.runsOn} org={org} accesstoken={accesstoken} onChange={onTeamsChange} />}
            {connector.whatsapp && <WhatsappSetup status={connector.whatsapp} org={org} accesstoken={accesstoken} onChange={onWhatsappChange} />}
            {!!connector.usedBy.length && (
                <Typography sx={{ fontSize: '11px', color: MUTED }}>Used by {connector.usedBy.join(', ')}</Typography>
            )}
        </ListRow>
    );
}

const FILTERS = [
    { id: 'all', label: 'All', match: () => true },
    { id: 'on', label: 'Working', match: c => ['connected', 'builtin'].includes(c.status) },
    { id: 'off', label: 'Off or incomplete', match: c => ['off', 'attention', 'unknown'].includes(c.status) },
    { id: 'planned', label: 'Planned', match: c => c.status === 'planned' },
];

function ElariomConnectors({ connectors, org, accesstoken, onTeamsChange, onWhatsappChange }) {
    const [ filter, setFilter ] = useState('all');
    const active = FILTERS.find(f => f.id === filter);
    const visible = connectors.filter(active.match);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <SectionTitle
                title='Connectors'
                blurb='Everything this workspace can read from and act on. Workflows and agents reach other systems only through these.'
                right={
                    <Tooltip title='Adding connectors from here comes later. Today they are set up in Settings or on the server.'>
                        <span><Button variant='contained' size='small' disabled>New connector</Button></span>
                    </Tooltip>
                }
            />
            <Box sx={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '13px' }}>
                {FILTERS.map(f => (
                    <Box key={f.id} role='button' tabIndex={0} onClick={() => setFilter(f.id)} onKeyDown={e => e.key === 'Enter' && setFilter(f.id)}
                        sx={{ cursor: 'pointer', color: filter === f.id ? INK : MUTED, fontWeight: filter === f.id ? 600 : 400, borderBottom: `1px solid ${filter === f.id ? INK : 'transparent'}`, paddingBottom: '2px', '&:hover': { color: INK } }}>
                        {f.label} · {connectors.filter(f.match).length}
                    </Box>
                ))}
            </Box>
            {CONNECTOR_GROUPS.map(group => {
                const items = visible.filter(c => c.group === group.id);
                if (!items.length) return null;
                return (
                    <Box key={group.id}>
                        <GroupTitle title={group.title} blurb={group.blurb} />
                        {items.map(c => <ConnectorRow key={c.id} connector={c} org={org} accesstoken={accesstoken} onTeamsChange={onTeamsChange} onWhatsappChange={onWhatsappChange} />)}
                    </Box>
                );
            })}
        </Box>
    );
}

export default ElariomConnectors;
